import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Calendar,
  Globe,
  CreditCard,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle2,
  AlertCircle,
  PauseCircle,
  PlayCircle,
  KeyRound,
  ExternalLink,
  Copy,
  Check,
  Plus,
  Sparkles,
  History,
  Layers,
  Server,
  FileCheck,
  Trash2,
} from 'lucide-react';
import {
  UserProfile,
  SiteData,
  BillingOrder,
  ShopierTransaction,
  AIVisibilitySubscription,
  UserSubscriptionRecord,
} from '../../types';
import { formatDateTR, calculateRemainingDays, addMonthsSafely } from '../../utils/dateUtils';
import { realtimeDbService } from '../../services/realtimeDbService';
import { useAuth } from '../../context/AuthContext';
import { getSiteDisplayAddress } from '../../utils/security';

interface UserDetailModalProps {
  user: UserProfile;
  sites: SiteData[];
  orders: BillingOrder[];
  transactions: ShopierTransaction[];
  onClose: () => void;
  onUserUpdated?: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  user,
  sites,
  orders,
  transactions,
  onClose,
  onUserUpdated,
}) => {
  const { currentUser: currentAdmin } = useAuth();
  const [copiedId, setCopiedId] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string>('');
  const [currentStatus, setCurrentStatus] = useState<'ACTIVE' | 'SUSPENDED'>(
    (user.status as string)?.toUpperCase() === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE'
  );

  // Subscriptions & Audit logs
  const [aiVisSub, setAiVisSub] = useState<AIVisibilitySubscription | null>(null);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [activeWebsiteSub, setActiveWebsiteSub] = useState<UserSubscriptionRecord | null>(null);

  // Manual Package Assignment Form State
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignProduct, setAssignProduct] = useState<
    'PLAN_CORP_ANNUAL' | 'PLAN_YACHT_ANNUAL' | 'AI_LOCAL_VISIBILITY' | 'AI_SEARCH_VISIBILITY'
  >('PLAN_CORP_ANNUAL');
  const [assignSiteId, setAssignSiteId] = useState<string>('');
  const [assignMonths, setAssignMonths] = useState<number>(12);
  const [assignPrice, setAssignPrice] = useState<number>(15000);
  const [assignReason, setAssignReason] = useState<string>('Super Admin yetkili paket ataması');

  const userSites = sites.filter(
    (s) => (s.ownerId === user.uid || (s as any).tenantId === user.uid) && !s.isDeleted && s.status !== 'DELETED'
  );
  const userOrders = orders.filter((o) => o.userId === user.uid || (o as any).tenantId === user.uid);
  const userTransactions = transactions.filter((t) => (t as any).userId === user.uid || (t as any).tenantId === user.uid);

  const [siteBeingDeleted, setSiteBeingDeleted] = useState<{ id: string; name: string } | null>(null);

  const handleAdminDeleteSite = async () => {
    if (!siteBeingDeleted || actionLoading) return;
    setActionLoading(true);
    setActionMessage('');
    try {
      await realtimeDbService.deleteSite(siteBeingDeleted.id, currentAdmin?.email || 'admin');
      setActionMessage(`"${siteBeingDeleted.name}" adlı website tamamen silindi. Website hakkı serbest bırakıldı.`);
      setSiteBeingDeleted(null);
      if (onUserUpdated) onUserUpdated();
    } catch (err: any) {
      setActionMessage(`Hata: ${err?.message || 'Website silinemedi.'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const now = Date.now();
  const userTrialEnd = user.trial?.trialEndDate || user.trial?.trialEndsAt || user.trialEndDate;
  const isTrialActive = user.trial?.trialStatus === 'ACTIVE' || user.trial?.isActive || Boolean(userTrialEnd && userTrialEnd > now);
  const trialDaysLeft = userTrialEnd ? calculateRemainingDays(userTrialEnd, now) : 0;

  useEffect(() => {
    if (userSites.length > 0 && !assignSiteId) {
      setAssignSiteId(userSites[0].id);
    }
  }, [userSites, assignSiteId]);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const [sub, logs, activeWebSub] = await Promise.all([
          realtimeDbService.getAIVisibilitySubscription(user.uid),
          realtimeDbService.getAdminAuditLogs(user.uid),
          realtimeDbService.findActiveWebsiteSubscription(user.uid),
        ]);
        setAiVisSub(sub);
        setAuditLogs(logs);
        setActiveWebsiteSub(activeWebSub);

        // Pre-select matching product if active
        if (activeWebSub) {
          if (activeWebSub.sector === 'YACHT_RENTAL' || activeWebSub.planId?.includes('YACHT')) {
            setAssignProduct('PLAN_YACHT_ANNUAL');
            setAssignPrice(36000);
          } else {
            setAssignProduct('PLAN_CORP_ANNUAL');
            setAssignPrice(15000);
          }
        }
      } catch (err) {
        console.error('Error loading user subscriptions/logs:', err);
      }
    };
    loadUserData();
  }, [user.uid]);

  const handleProductChange = (
    prod: 'PLAN_CORP_ANNUAL' | 'PLAN_YACHT_ANNUAL' | 'AI_LOCAL_VISIBILITY' | 'AI_SEARCH_VISIBILITY'
  ) => {
    setAssignProduct(prod);
    if (prod === 'PLAN_CORP_ANNUAL') {
      setAssignPrice(15000);
      setAssignMonths(12);
    } else if (prod === 'PLAN_YACHT_ANNUAL') {
      setAssignPrice(36000);
      setAssignMonths(12);
    } else if (prod === 'AI_LOCAL_VISIBILITY' || prod === 'AI_SEARCH_VISIBILITY') {
      setAssignPrice(5000);
      setAssignMonths(12);
    }
  };

  const handleAssignPackage = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    setActionMessage('');
    try {
      const start = Date.now();
      const end = addMonthsSafely(start, assignMonths);
      await realtimeDbService.assignManualSubscription({
        adminUid: currentAdmin?.uid || 'SUPER_ADMIN',
        adminEmail: currentAdmin?.email || 'admin@adiosoft.com',
        tenantId: user.uid,
        customerName: user.displayName || user.companyName || user.email,
        product: assignProduct,
        siteId: assignSiteId,
        startDate: start,
        endDate: end,
        status: 'ACTIVE',
        price: assignPrice,
        reason: assignReason,
      });
      setIsAssignModalOpen(false);
      setActionMessage(`${assignProduct} paketi başarıyla tanımlandı ve aktifleştirildi.`);

      // Reload logs and subscriptions
      const [sub, logs] = await Promise.all([
        realtimeDbService.getAIVisibilitySubscription(user.uid),
        realtimeDbService.getAdminAuditLogs(user.uid),
      ]);
      setAiVisSub(sub);
      setAuditLogs(logs);
      if (onUserUpdated) onUserUpdated();
    } catch (err: any) {
      setActionMessage(`Hata: ${err?.message || 'Paket ataması başarısız.'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const copyUserId = () => {
    navigator.clipboard.writeText(user.uid);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleToggleSuspend = async () => {
    setActionLoading(true);
    setActionMessage('');
    try {
      const nextStatus = currentStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
      await realtimeDbService.updateUserStatus(user.uid, nextStatus);
      setCurrentStatus(nextStatus);
      setActionMessage(`Kullanıcı durumu "${nextStatus === 'ACTIVE' ? 'Aktif' : 'Askıda'}" olarak güncellendi.`);
      if (onUserUpdated) onUserUpdated();
    } catch (err: any) {
      setActionMessage(`Hata: ${err?.message || 'İşlem başarısız.'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendTrial = async (daysToAdd = 7) => {
    setActionLoading(true);
    setActionMessage('');
    try {
      const currentEnd = user.trial?.trialEndDate || user.trial?.trialEndsAt || user.trialEndDate;
      const baseTime = currentEnd && currentEnd > now ? currentEnd : now;
      const newEndsAt = baseTime + daysToAdd * 86400000;
      await realtimeDbService.updateUserTrial(user.uid, {
        trialStatus: 'ACTIVE',
        isActive: true,
        trialEndDate: newEndsAt,
        trialEndsAt: newEndsAt,
      });
      setActionMessage(`Trial süresi ${daysToAdd} gün uzatıldı. Yeni bitiş: ${formatDateTR(newEndsAt)}`);
      if (onUserUpdated) onUserUpdated();
    } catch (err: any) {
      setActionMessage(`Hata: ${err?.message || 'Trial uzatılamadı.'}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendPasswordReset = async () => {
    setActionLoading(true);
    setActionMessage('');
    try {
      await realtimeDbService.requestPasswordResetEmail(user.email);
      setActionMessage(`${user.email} adresine şifre sıfırlama bağlantısı gönderildi.`);
    } catch (err: any) {
      setActionMessage(`Şifre sıfırlama talebi iletildi (${user.email}).`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6">
      <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-base shadow-xs">
              {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-slate-900">
                  {user.displayName || 'İsimsiz Kullanıcı'}
                </h3>
                <span
                  className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                    currentStatus === 'ACTIVE'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border border-rose-200'
                  }`}
                >
                  {currentStatus === 'ACTIVE' ? 'Aktif' : 'Askıda'}
                </span>
                {user.role === 'SUPER_ADMIN' && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                    Super Admin
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 font-mono mt-0.5">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsAssignModalOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Paket Tanımla</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-200/70 text-slate-400 hover:text-slate-700 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {actionMessage && (
            <div className="p-3.5 rounded-xl bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-900 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
              <span>{actionMessage}</span>
            </div>
          )}

          {/* Section 1: User Overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Firma Adı
              </span>
              <div className="text-xs font-bold text-slate-900 mt-1 truncate">
                {user.companyName || '-'}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Telefon
              </span>
              <div className="text-xs font-mono font-bold text-slate-900 mt-1 truncate">
                {user.phoneNumber || user.phone || '-'}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Kayıt Tarihi
              </span>
              <div className="text-xs font-bold text-slate-900 mt-1">
                {formatDateTR(user.createdAt)}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                Trial Durumu
              </span>
              <div className="text-xs font-bold text-slate-900 mt-1 flex items-center gap-1.5">
                {isTrialActive ? (
                  <span className="text-amber-600">
                    Aktif ({trialDaysLeft > 0 ? `${trialDaysLeft} gün kaldı` : 'Bitti'})
                  </span>
                ) : (
                  <span className="text-slate-500">Kullanılmıyor</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Web Siteleri */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-blue-600" />
              Kullanıcı Web Siteleri ({userSites.length})
            </h4>
            {userSites.length === 0 ? (
              <div className="p-4 rounded-xl bg-slate-50 text-xs text-slate-500 text-center border border-slate-100">
                Bu kullanıcıya ait henüz oluşturulmuş bir web sitesi bulunmuyor.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white text-xs">
                {userSites.map((site) => (
                  <div key={site.id} className="p-3.5 flex items-center justify-between gap-3">
                    <div className="space-y-0.5 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{site.title || site.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono truncate">
                        {getSiteDisplayAddress(site)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          site.status === 'PUBLISHED'
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-amber-50 text-amber-700'
                        }`}
                      >
                        {site.status === 'PUBLISHED' ? 'Yayında' : site.status}
                      </span>
                      <span className="text-[11px] text-slate-500 font-medium">
                        Bitiş: {formatDateTR(site.subscription?.expireDate)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSiteBeingDeleted({ id: site.id, name: site.title || site.name })}
                        className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition"
                        title="Website'i Tamamen Sil"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 3: AI Search Visibility Aboneliği */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-purple-600" />
              AI Arama Görünürlüğü (AI_SEARCH_VISIBILITY)
            </h4>
            {aiVisSub ? (
              <div className="p-4 rounded-xl border border-purple-100 bg-purple-50/50 space-y-3 text-xs">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900">Durum:</span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        aiVisSub.status === 'active'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {aiVisSub.status === 'active' ? 'Aktif' : aiVisSub.status}
                    </span>
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-white border border-purple-200 text-purple-800">
                      5.000 TL / yıl
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    Ödeme Sağlayıcı: {(aiVisSub as any).paymentProvider || 'SHOPIER/ADMIN'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-[11px] text-slate-600">
                  <div>
                    <span className="text-slate-400">Başlangıç:</span>{' '}
                    <strong>{formatDateTR(aiVisSub.startDate)}</strong>
                  </div>
                  <div>
                    <span className="text-slate-400">Bitiş Tarihi:</span>{' '}
                    <strong>{formatDateTR(aiVisSub.endDate)}</strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-slate-50 text-xs text-slate-500 text-center border border-slate-100 flex items-center justify-between">
                <span>Aktif AI Arama Görünürlüğü aboneliği bulunmuyor.</span>
                <button
                  type="button"
                  onClick={() => {
                    handleProductChange('AI_SEARCH_VISIBILITY');
                    setIsAssignModalOpen(true);
                  }}
                  className="px-2.5 py-1 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-semibold"
                >
                  AI Paketi Tanımla
                </button>
              </div>
            )}
          </div>

          {/* Section 4: Ödemeler ve Siparişler */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <CreditCard className="w-3.5 h-3.5 text-blue-600" />
              Ödemeler ve Siparişler ({userOrders.length + userTransactions.length})
            </h4>
            {userTransactions.length === 0 && userOrders.length === 0 ? (
              <div className="p-4 rounded-xl bg-slate-50 text-xs text-slate-500 text-center border border-slate-100">
                Henüz kayıtlı ödeme işlemi bulunmuyor.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white text-xs max-h-48 overflow-y-auto">
                {userTransactions.map((tx) => (
                  <div key={tx.id} className="p-3 flex items-center justify-between">
                    <div className="space-y-0.5">
                      <div className="font-semibold text-slate-900">
                        {tx.planName || 'Adiosoft Hizmet Ödemesi'}
                      </div>
                      <div className="text-[11px] text-slate-400 font-mono">
                        Sipariş: {tx.orderId || tx.id}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-slate-900">
                        {tx.amount.toLocaleString('tr-TR')} {tx.currency || 'TL'}
                      </div>
                      <div className="text-[10px] text-emerald-600 font-semibold">
                        {tx.status === 'SUCCESS' ? 'Başarılı' : tx.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 6: Yönetici Denetim Kayıtları (Audit Logs) */}
          <div className="space-y-2.5">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
              <History className="w-3.5 h-3.5 text-slate-600" />
              Yönetici İşlem & Denetim Günlüğü ({auditLogs.length})
            </h4>
            {auditLogs.length === 0 ? (
              <div className="p-4 rounded-xl bg-slate-50 text-xs text-slate-500 text-center border border-slate-100">
                Bu müşteri için henüz yönetici audit kaydı bulunmuyor.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white text-xs max-h-40 overflow-y-auto">
                {auditLogs.map((log) => (
                  <div key={log.id} className="p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{log.product} - {log.action}</span>
                      <span className="text-[10px] text-slate-400">{formatDateTR(log.createdAt)}</span>
                    </div>
                    <div className="text-[11px] text-slate-600">
                      Gerekçe: {log.reason} | Yönetici: {log.adminEmail || log.adminUid}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal: Manual Package Assignment */}
        {isAssignModalOpen && (
          <div className="fixed inset-0 z-60 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-slate-200">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                    <Plus className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Müşteriye Paket Tanımla</h3>
                    <p className="text-[11px] text-slate-500">{user.displayName || user.email}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAssignModalOpen(false)}
                  className="p-1 hover:bg-slate-100 rounded text-slate-400"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Single Website License Check (Requirement 26 & 27) */}
              {activeWebsiteSub && (
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Mevcut Aktif Website Aboneliği:</span>{' '}
                    <span>{activeWebsiteSub.planName || activeWebsiteSub.planId}</span> (Bitiş:{' '}
                    {formatDateTR(activeWebsiteSub.expireDate || activeWebsiteSub.endDate)})
                    <p className="text-[11px] text-blue-700 mt-0.5">
                      Kullanıcı başına aynı anda en fazla 1 aktif website paketi tanımlanabilir.
                      Aynı paketi seçerseniz abonelik süresi uzatılacaktır.
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleAssignPackage} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paket / Ürün Seçimi</label>
                  <select
                    value={assignProduct}
                    onChange={(e) => handleProductChange(e.target.value as any)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900"
                  >
                    <option
                      value="PLAN_CORP_ANNUAL"
                      disabled={Boolean(activeWebsiteSub && (activeWebsiteSub.sector === 'YACHT_RENTAL' || activeWebsiteSub.planId?.includes('YACHT')))}
                    >
                      Kurumsal Web Sitesi Paketi (15.000 TL / yıl)
                      {activeWebsiteSub && (activeWebsiteSub.sector === 'YACHT_RENTAL' || activeWebsiteSub.planId?.includes('YACHT'))
                        ? ' — (Engellendi: Aktif Yat Paketi Var)'
                        : ''}
                    </option>
                    <option
                      value="PLAN_YACHT_ANNUAL"
                      disabled={Boolean(activeWebsiteSub && (activeWebsiteSub.sector === 'CORPORATE' || activeWebsiteSub.planId?.includes('CORP')))}
                    >
                      Lüks Yat Kiralama Paketi (36.000 TL / yıl)
                      {activeWebsiteSub && (activeWebsiteSub.sector === 'CORPORATE' || activeWebsiteSub.planId?.includes('CORP'))
                        ? ' — (Engellendi: Aktif Kurumsal Paket Var)'
                        : ''}
                    </option>
                    <option value="AI_LOCAL_VISIBILITY">AI Yerel İşletme Görünürlüğü (5.000 TL / yıl)</option>
                    <option value="AI_SEARCH_VISIBILITY">AI Arama Görünürlüğü (5.000 TL / yıl)</option>
                  </select>
                </div>

                {/* Conflict warning if admin selected conflicting plan */}
                {Boolean(
                  (activeWebsiteSub?.sector === 'CORPORATE' && assignProduct === 'PLAN_YACHT_ANNUAL') ||
                  (activeWebsiteSub?.sector === 'YACHT_RENTAL' && assignProduct === 'PLAN_CORP_ANNUAL')
                ) && (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                    <span>
                      Bu hesabınızda aktif bir website aboneliği bulunmaktadır ({activeWebsiteSub?.planName}).
                      Aynı anda yalnızca bir website paketi kullanılabilir.
                    </span>
                  </div>
                )}

                {userSites.length > 0 && (
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">İlişkili Web Sitesi</label>
                    <select
                      value={assignSiteId}
                      onChange={(e) => setAssignSiteId(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900"
                    >
                      {userSites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.title || s.name} ({s.customDomain || s.slug})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Süre (Ay)</label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={assignMonths}
                      onChange={(e) => setAssignMonths(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Tutar (TRY)</label>
                    <input
                      type="number"
                      min={0}
                      value={assignPrice}
                      onChange={(e) => setAssignPrice(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 font-mono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Atama Nedeni / Gerekçe (Audit)</label>
                  <input
                    type="text"
                    required
                    value={assignReason}
                    onChange={(e) => setAssignReason(e.target.value)}
                    placeholder="Örn: Yıllık kurumsal sözleşme onayı, banka havalesi vb."
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900"
                  />
                </div>

                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[11px] leading-relaxed">
                  <strong>Güvenlik ve Denetim:</strong> Bu işlem <code>paymentProvider = 'ADMIN'</code> olarak kaydedilir ve yönetici denetim günlüğünde loglanır.
                </div>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => setIsAssignModalOpen(false)}
                    className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={
                      actionLoading ||
                      Boolean(
                        (activeWebsiteSub?.sector === 'CORPORATE' && assignProduct === 'PLAN_YACHT_ANNUAL') ||
                        (activeWebsiteSub?.sector === 'YACHT_RENTAL' && assignProduct === 'PLAN_CORP_ANNUAL')
                      )
                    }
                    className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 shadow-sm"
                  >
                    <FileCheck className="w-4 h-4" />
                    <span>{actionLoading ? 'Kaydediliyor...' : 'Paketi Tanımla ve Aktifleştir'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={copyUserId}
            className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1.5 font-mono"
          >
            {copiedId ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            UID: {user.uid.slice(0, 10)}...
          </button>

          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => handleExtendTrial(7)}
              disabled={actionLoading}
              className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition-colors disabled:opacity-50"
            >
              +7 Gün Trial
            </button>

            <button
              type="button"
              onClick={handleSendPasswordReset}
              disabled={actionLoading}
              className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg shadow-2xs transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <KeyRound className="w-3.5 h-3.5" />
              Şifre Sıfırla
            </button>

            <button
              type="button"
              onClick={handleToggleSuspend}
              disabled={actionLoading}
              className={`px-4 py-1.5 text-xs font-semibold rounded-lg shadow-2xs transition-colors disabled:opacity-50 flex items-center gap-1.5 ${
                currentStatus === 'ACTIVE'
                  ? 'text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200'
                  : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
              }`}
            >
              {currentStatus === 'ACTIVE' ? (
                <>
                  <PauseCircle className="w-3.5 h-3.5" />
                  Kullanıcıyı Askıya Al
                </>
              ) : (
                <>
                  <PlayCircle className="w-3.5 h-3.5" />
                  Kullanıcıyı Aktif Et
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Admin Site Delete Confirmation Modal */}
      {siteBeingDeleted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 border border-slate-100 text-slate-900">
            <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-bold text-slate-900">Website tamamen silinsin mi?</h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Bu işlem <strong className="text-slate-900">{siteBeingDeleted.name}</strong> adlı web sitesinin içeriğini, sayfalarını, tasarımını ve verilerini kalıcı olarak kaldıracaktır. Kullanıcının aboneliği silinmez; website hakkı serbest kalır.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setSiteBeingDeleted(null)}
                disabled={actionLoading}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleAdminDeleteSite}
                disabled={actionLoading}
                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition flex items-center gap-1.5 shadow-sm disabled:opacity-50"
              >
                {actionLoading ? 'Siliniyor...' : "Website'i Tamamen Sil"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
